import React from "react";
import { Glyphicon } from "react-bootstrap";
import FontAwesome from "react-fontawesome";
import { string } from "yup";
import { tooltipProps } from "../util/tooltip";

// vite appears to be doing some weird inlining of small svg's that don't survive the production build.
// So instead of importing via js, we just put the images in the 'public/assets' directory and put the correct link here
const chartAligned = "/assets/chart_aligned.svg";
const chartJumpy = "/assets/chart_jumpy.svg";

type CustomIcon = "chartAligned" | "chartJumpy";
type IconProps = {
  glyphicon?: string;
  fontAwesome?: string;
  custom?: CustomIcon;
  name?: keyof typeof icons;
  title?: string;
};

export const Icon = ({
  glyphicon,
  fontAwesome,
  custom,
  name,
  title,
  ...props
}: IconProps) => {
  // Render `title` as a react-tooltip anchor (shows on mobile taps) instead of
  // an HTML title (desktop-hover only). tip forwards through Glyphicon/
  // FontAwesome/SVGIcon, which all spread props onto their DOM element.
  const tip = tooltipProps(title);
  if (glyphicon) {
    return <Glyphicon glyph={glyphicon} {...tip} {...props} />;
  }
  if (fontAwesome) {
    return <FontAwesome name={fontAwesome} {...tip} {...props} />;
  }
  if (custom) {
    switch (custom) {
      case "chartAligned":
        return <SVGIcon svg={chartAligned} {...tip} {...props} />;
      case "chartJumpy":
        return <SVGIcon svg={chartJumpy} {...tip} {...props} />;
      default:
        return <span>?</span>;
    }
  }
  if (name) {
    /*
            TODO: eventually, it would be nice to have all icons "proxied" through this icon map. This way, we can give
            the icons names that are easy to remember and abstract away the icon libraries we use
         */
    const icon = icons[name];
    if (icon) {
      return React.cloneElement(icon, { ...tip, ...props });
    }
    console.warn(
      `Used icon with unknown name: ${name}. Add it to the icons map in Icon.js.`,
    );
    return <>"???"</>;
  }
  return null;
};

// @ts-ignore
export const SVGIcon = ({ svg, ...props }) => (
  <span
    style={{
      background: `url(${svg}) no-repeat top left`,
      backgroundSize: "contain",
      width: "14px",
      height: "14px",
      display: "inline-block",
    }}
    {...props}
  ></span>
);

const icons = {
  delete: <Icon fontAwesome="trash" />,
  edit: <Icon fontAwesome="edit" />,
  add: <Icon fontAwesome="plus" />,
  info: <Icon fontAwesome="info-circle" />,
  save: <Icon fontAwesome="save" />,
  check: <Icon fontAwesome="check" />,
  warning: <Icon fontAwesome="warning" />,
  "chevron-down": <Icon fontAwesome="chevron-down" />,
  "chevron-right": <Icon fontAwesome="chevron-right" />,
  "arrow-up": <Icon fontAwesome="arrow-up" />,
  "arrow-down": <Icon fontAwesome="arrow-down" />,
  scale: <Icon fontAwesome="line-chart" />,
  controller: <Icon fontAwesome="server" />,
  notification: <Icon fontAwesome="bell" />,
  share: <Icon fontAwesome="share" />,
  user: <Icon fontAwesome="user" />,
  "shop.admin.confirmation": <Icon fontAwesome="envelope-o" />,
  "shop.admin.advance": <Icon fontAwesome="gavel" />,
  "shop.admin.release": <Icon fontAwesome="sign-out" />,
  "shop.admin.offer": <Icon fontAwesome="thumbs-up" />,
  archive: <Icon fontAwesome="archive" />,
  forward: <Icon fontAwesome="arrow-right" />,
  region: <Icon fontAwesome="globe" />,
  watch: <Icon fontAwesome="eye" />,
  analysis: <Icon fontAwesome={"search"} />,
  pending: <Icon fontAwesome={"clock-o"} />,
  stop: <Icon fontAwesome={"stop"} />,
};
